import React, { useEffect, useMemo, useState } from 'react';
import { CellProps, Column, useExpanded, useFilters, useTable } from 'react-table';
import { useAccountStore } from '../../../../components/Stores/accountStore';
import { useMaterialsStore } from '../../../../components/Stores/MaterialsStore';
import { IFutureOrder, ISupplierExposure } from '../../../../sp-types/FutureOrderType';
import { FormatToShortPriceTemplate, formatToShortString } from '../../../../utils/FormatValue';
import { months } from '../../../../utils/MonthYear/monthYear';
import { FutureOrderIdLink } from './FutureOrderIdLink';
import { deleteFutureOrder } from '../../../../sp-firebase/futureOrderFB';
import { modifyCompletedOrder } from '../../AddNewFutureOrder/AddNewFutureOrderUtils';
import { useFutureOrderStore } from '../../../../components/Stores/FutureOrdersStore';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { ConfirmDelete } from '../../../../components/UI-Elements/FileUpload/ErrorComponents/ConfirmDelete';
import { getUnitObj } from '../../../../utils/MassUnits/massUnitsUtils';

interface Props {
  futureOrders: IFutureOrder[];
  currentMaterialId: string;
  setIsLoading: (_isLoading: boolean) => void;
  refetchOrders: () => void;
  isEditEnabled: boolean;
}

const FutureOrderTableByOrders: React.FC<Props> = ({
  futureOrders: allFutureOrders,
  currentMaterialId,
  setIsLoading,
  refetchOrders,
  isEditEnabled,
}) => {
  const { materialsMap } = useMaterialsStore();
  const { materialColorDictionary, account } = useAccountStore();
  const [futureOrdersToShow, setFutureOrdersToShow] = useState<IFutureOrder[]>(allFutureOrders);

  const { setEditedFutureOrder } = useFutureOrderStore();
  const navigate = useNavigate();

  useEffect(() => {
    setFutureOrdersToShow(
      currentMaterialId === 'general' ? allFutureOrders : filterOrdersByMaterialId(allFutureOrders, currentMaterialId),
    );
  }, [allFutureOrders, currentMaterialId]);

  const columns: Column<IFutureOrder>[] = useMemo(
    () => [
      {
        Header: 'Order ID',
        accessor: (row) => row,
        Cell: ({ value }: CellProps<IFutureOrder>) => {
          return (
            <FutureOrderIdLink futureOrderType={value.futureOrderType} orderId={value.orderId} orderUuid={value.uuid} />
          );
        },
      },
      {
        Header: 'Material',
        accessor: 'supplierExposure',
        Cell: ({ value }: CellProps<IFutureOrder>) => {
          if (value.length === 1) {
            return <span>{materialsMap[value[0].materialId].name}</span>;
          } else {
            return <span>{`${value.length} Materials`}</span>;
          }
        },
      },
      {
        Header: 'Order Date',
        accessor: (row) => row.futureOrderDate?.toDate().toLocaleDateString('en-CA') || '',
        Cell: ({ value }: CellProps<IFutureOrder>) => <span>{value}</span>,
      },
      {
        Header: 'Delivery date(s)',
        accessor: (row) => {
          if (row.orderDetails.deliveryDates) {
            return row.orderDetails.deliveryDates.map((date) => `${months[date.month - 1]} ${date.year}`).join('-');
          }
          return '';
        },
        Cell: ({ value }: CellProps<IFutureOrder>) => <span>{value}</span>,
      },
      {
        Header: 'Quantity',

        accessor: (row) => row,
        Cell: ({ value }: CellProps<IFutureOrder>) => {
          // const totalQuantity = value.supplierExposure.reduce(
          //   (acc: number, material: ISupplierExposure) => acc + material.weightInProduct * value.orderDetails.quantity,
          //   0,
          // );
          return <span>-</span>; // again,sum quantity has no meaning
        },
      },
      {
        Header: 'Total price',
        accessor: (row) => FormatToShortPriceTemplate(row.orderDetails.quantity * (row.orderDetails.pricePerUnit || 1)),
        Cell: ({ value }: CellProps<IFutureOrder>) => <span>{value}</span>,
      },
      {
        Header: 'Adjustment',
        accessor: (row) => {
          const adjustments = row.supplierExposure.map((material) => material.adjustment);
          const allSame = adjustments.every((adj) => adj === adjustments[0]);
          return allSame ? adjustments[0] : 'multi';
        },
        Cell: ({ value, row }: CellProps<IFutureOrder>) => (
          <div className="flex justify-between ">
            <span>{value}</span>

            {isEditEnabled && (
              <div className="flex h-5 pe-2">
                <Button onClick={() => editOrder(row.original)} className="h-6 w-4 px-3 py-1">
                  <Edit />
                </Button>
                <Button onClick={() => setConfirmDelete(row.original)} className="h-6 w-4 px-3 py-1">
                  <Delete />
                </Button>
                <img
                  className={`${row.isExpanded ? '-rotate-90' : 'rotate-90'} transition-transform h-7 w-7`}
                  title="down"
                  alt="down"
                  src="../assets/icons/flat/chevron-right_purple.svg"
                ></img>
              </div>
            )}
          </div>
        ),
      },
    ],
    [materialsMap],
  );

  const tableInstance = useTable(
    {
      columns,
      data: futureOrdersToShow,
    },
    useFilters,
    useExpanded,
  );

  const { getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

  const [confirmDelete, setConfirmDelete] = React.useState<IFutureOrder | undefined>();
  const deleteOrder = async (order: IFutureOrder) => {
    setIsLoading(true);
    setConfirmDelete(undefined);

    await deleteFutureOrder(order.uuid || '', account?.id);

    refetchOrders();
    setIsLoading(false);
  };

  const editOrder = async (order: IFutureOrder) => {
    setIsLoading(true);

    await modifyCompletedOrder(order, account?.id || '', (futureOrder) => setEditedFutureOrder(futureOrder), navigate);

    setIsLoading(false);
  };

  return (
    <div className="flex flex-1  overflow-y-auto ">
      <table className=" flex-1  bg-red overflow-scroll">
        <thead className=" mb-[1rem]">
          {headerGroups.map((headerGroup, index) => (
            <tr key={index} className="">
              {headerGroup.headers.map((column, index) => (
                <th
                  key={index}
                  className="bg-gray-50 text-secondary_text font-normal text-base py-[0.75rem] ps-[1rem] "
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className=" border-white ">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <React.Fragment key={row.id}>
                <tr
                  {...row.getRowProps()}
                  onClick={() => row.toggleRowExpanded()}
                  className="bg-white"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: row.isExpanded ? '#f5f5f5' : 'white',
                    borderTop: '6px solid #ffffff',
                    borderBottom: '6px solid #ffffff',
                  }}
                >
                  {row.cells.map((cell, index) => (
                    <td key={index} className=" font-medium  ">
                      <div
                        style={
                          index === 0
                            ? { borderLeft: '1px solid #f0f0f0' }
                            : index === row.cells.length - 1
                            ? { borderRight: '1px solid #f0f0f0' }
                            : {}
                        }
                        className="border-y border-gray-100 m-0 py-[0.75rem] ps-[0.75rem] whitespace-nowrap overflow-hidden text-ellipsis"
                      >
                        {cell.render('Cell')}
                      </div>
                    </td>
                  ))}
                </tr>
                {row.isExpanded
                  ? row.original.supplierExposure.map((material) => (
                      <tr key={material.materialId} className=" bg-gray-50 text-sm border-y border-y-white    ">
                        <td className={`  `}>
                          <div
                            style={{
                              borderLeft: `6px solid ${materialColorDictionary[material.materialId]}`,
                            }}
                            className="ps-[0.7rem] py-[1rem]"
                          >
                            <FutureOrderIdLink
                              futureOrderType={row.original.futureOrderType}
                              orderId={row.original.orderId}
                              orderUuid={row.original.uuid}
                            />
                          </div>
                        </td>
                        <td className="ps-[1rem] py-[1rem]">{materialsMap[material.materialId].name}</td>
                        <td className="ps-[1rem] py-[1rem]">
                          {row.original.futureOrderDate?.toDate().toLocaleDateString('en-CA') || 'date missing'}
                        </td>
                        <td className="ps-[1rem] py-[1rem]">
                          {material.exposureCalendar.length > 1
                            ? `${months[material.exposureCalendar[0].date.month - 1]} ${
                                material.exposureCalendar[0].date.year
                              }- ${
                                months[material.exposureCalendar[material.exposureCalendar.length - 1].date.month - 1]
                              } ${material.exposureCalendar[material.exposureCalendar.length - 1].date.year}`
                            : ''}
                        </td>
                        <td className="ps-[1rem] py-[1rem]">
                          {formatToShortString(material.weightInProduct * row.original.orderDetails.quantity)}{' '}
                          {getUnitObj(materialsMap[material.materialId]?.unit).shortForm}
                        </td>
                        <td className="ps-[1rem] py-[1rem]">
                          {FormatToShortPriceTemplate(
                            material.basePrice * material.weightInProduct * row.original.orderDetails.quantity,
                          )}
                        </td>
                        <td className="ps-[1rem] py-[1rem]">{material.adjustment}</td>
                      </tr>
                    ))
                  : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {confirmDelete && (
        <ConfirmDelete
          onAction={() => deleteOrder(confirmDelete)}
          onCancel={() => setConfirmDelete(undefined)}
          title="Confirmation"
          description={`Are you sure you want to delete order: ${confirmDelete.orderId}?`}
          actionText="Delete"
        />
      )}
    </div>
  );
};

export default FutureOrderTableByOrders;

const filterOrdersByMaterialId = (futureOrders: IFutureOrder[], materialId: string): IFutureOrder[] =>
  futureOrders.filter(
    (order) =>
      order.totalExposure?.some((exposure) => exposure.materialId === materialId) ||
      order.supplierExposure.some((supplier) => supplier.materialId === materialId),
  );
